import Vue from 'vue';
import VueRouter from 'vue-router';
// import Home from '@/views/home/Home.vue';
import HomeCover from '@/views/home/HomeCover.vue';
import LayoutView from '@/views/LayoutView.vue';
import ViewTo404 from '@/views/ViewTo404.vue';
import { setCookie, removeCookie } from '@/utils/cookie';
import { isRelease } from '@/const/host';
import i18n, { ZH } from '@/locales';

Vue.use(VueRouter);

const originalPush = VueRouter.prototype.push
// 解决ElementUI导航栏中的vue-router在3.0版本以上重复点菜单报错问题
VueRouter.prototype.push = function push(location) {
	return originalPush.call(this, location).catch(err => err)
}

// 支持的语言列表
const supportedLocales = ['en'];

/**
 * 将指定组件设置自定义名称
 *
 * @param {String} name 组件自定义名称
 * @param {Component | Promise<Component>} component
 * @return {Component}
 */
function createCustomComponent(name, component) {
	return {
		name,
		data() {
			return { component: null };
		},
		async created() {
			if (component instanceof Promise) {
				try {
					const module = await component;
					this.component = module?.default;
				} catch (error) {
					console.error(`can not resolve component ${name}, error:`, error);
				}

				return;
			}
			this.component = component;
		},
		render(h) {
			return this.component ? h(this.component) : null;
		}
	};
}

// 定义基础子路由配置
const baseChildRoutes = [
	{
		path: 'index',
		name: 'home',
		component: HomeCover,
		meta: {
			title: '首页',
			deep: 0
		}
	},
	{
		path: 'quarterly_performance',
		name: 'quarterly_performance',
		component: createCustomComponent(
			'quarterly_performance',
			import(/* webpackChunkName: "quarterly_performance" */ '../views/quarterly_performance/index.vue')
		),
		meta: {
			title: '定期报告',
			deep: 1
		}
	},
	{
		path: 'finance_report',
		name: 'finance_report',
		component: createCustomComponent(
			'finance_report',
			import(/* webpackChunkName: "finance_report" */ '../views/finance_report/index.vue')
		),
		meta: {
			title: '财务报表',
			deep: 1
		}
	},
	{
		path: 'issuer_information',
		name: 'issuer_information',
		component: createCustomComponent(
			'issuer_information',
			import(/* webpackChunkName: "issuer_information" */ '../views/issuer_information/index.vue')
		),
		meta: {
			title: '招股说明书',
			deep: 1
		}
	},
	{
		path: 'business_rules',
		name: 'business_rules',
		component: createCustomComponent(
			'business_rules',
			import(/* webpackChunkName: "business_rules" */ '../views/business_rules/index.vue')
		),
		meta: {
			title: '公司治理',
			deep: 1
		}
	},
	{
		path: 'question',
		name: 'question',
		component: createCustomComponent(
			'question',
			import(/* webpackChunkName: "question" */ '../views/question/index.vue')
		),
		meta: {
			title: '常见问题',
			deep: 1
		}
	},
	{
		path: 'finance_data',
		name: 'finance_data',
		component: createCustomComponent(
			'finance_data',
			import(/* webpackChunkName: "finance" */ '../views/finance_data/index.vue')
		),
		meta: {
			title: '财务报表',
			deep: 1
		}
	},
	{
		path: 'announcement',
		name: 'announcement',
		component: createCustomComponent(
			'announcement',
			import(/* webpackChunkName: "announcement" */ '../views/announcement/index.vue')
		),
		meta: {
			title: '临时公告',
			deep: 1
		}
	},
	{
		path: 'announcement/search',
		name: 'announcementSearch',
		component: createCustomComponent(
			'announcementSearch',
			import(/* webpackChunkName: "announcement" */ '../views/announcement/search.vue')
		),
		meta: {
			title: '搜索',
			deep: 2
		}
	},
	{
		path: 'contact',
		name: 'contact',
		component: createCustomComponent(
			'contact',
			import(/* webpackChunkName: "contact" */ '../views/contact/index.vue')
		),
		meta: {
			title: '联系方式',
			deep: 1
		}
	},
	{
		path: 'news',
		name: 'news',
		component: createCustomComponent(
			'news',
			import(/* webpackChunkName: "news" */ '../views/news/index.vue')
		),
		meta: {
			title: '新闻资讯',
			deep: 1
		}
	},
	{
		path: 'news/detail',
		name: 'newsDetail',
		component: createCustomComponent(
			'newsDetail',
			import(/* webpackChunkName: "newsDetail" */ '../views/news/newsDetail.vue')
		),
		meta: {
			title: '新闻详情',
			deep: 2
		}
	},
	{
		path: 'investment',
		name: 'investment',
		component: createCustomComponent(
			'investment',
			import(/* webpackChunkName: "investment" */ '../views/investment/index.vue')
		),
		meta: {
			title: '投关互动',
			deep: 1
		}
	},
	{
		path: 'analyst',
		name: 'analyst',
		component: createCustomComponent(
			'analyst',
			import(/* webpackChunkName: "analyst" */ '../views/analyst/index.vue')
		),
	},
	{
		path: 'report',
		name: 'report',
		component: createCustomComponent(
			'report',
			import(/* webpackChunkName: "report" */ '../views/report/index.vue')
		),
	},
	{
		path: 'sustain',
		name: 'sustain',
		component: createCustomComponent(
			'sustain',
			import(/* webpackChunkName: "sustain" */ '../views/sustain/index.vue')
		),
	},
	{
		path: 'presentation',
		name: 'presentation',
		component: createCustomComponent(
			'presentation',
			import(/* webpackChunkName: "presentation" */ '../views/presentation/index.vue')
		),
	},
	{
		path: 'quote',
		name: 'quote',
		component: createCustomComponent(
			'quote',
			import(/* webpackChunkName: "quote" */ '../views/quote/index.vue')
		),
	},
];

// 生成路由配置
const routes = [
	// 简体路由（无语言前缀）
	{
		path: '/',
		component: LayoutView,
		redirect: '/index',
		children: baseChildRoutes.map(route => ({
			...route,
			path: `/${route.path}`,
			meta: {
				...route.meta,
				locale: 'zh-cn'
			}
		}))
	},
	// 多语言路由（使用 :lang 参数）
	{
		path: '/:lang',
		component: LayoutView,
		redirect: to => `/${to.params.lang}/index`,
		children: baseChildRoutes.map(route => ({
			...route,
			path: route.path,
			name: route.name, // 使用相同的名称，通过路径区分
			meta: {
				...route.meta,
				locale: 'dynamic' // 从路由参数中获取
			}
		}))
	},
	// leave_word路由 - 简体
	{
		path: '/leave_word',
		name: 'leaveWord',
		component: () => import(/* webpackChunkName: "leave_word" */ '../views/leave_word/index'),
		meta: {
			title: '投资者留言',
			locale: 'zh-cn'
		}
	},
	// leave_word路由 - 多语言
	{
		path: '/:lang/leave_word',
		name: 'leaveWord', // 使用相同的名称，通过路径区分
		component: () => import(/* webpackChunkName: "leave_word" */ '../views/leave_word/index'),
		meta: {
			title: '投资者留言',
			locale: 'dynamic'
		}
	}
];

// 添加404路由
routes.push({
	path: '*',
	name: '404',
	component: ViewTo404,
	meta: {
		title: '抱歉，页面加载失败!',
		requireAuth: false
	}
});

const router = new VueRouter({
	mode: 'history',
	base: process.env.BASE_URL,
	routes,
	scrollBehavior(to, from, savedPosition) {
		if (savedPosition) {
			return savedPosition;
		} else {
			return { x: 0, y: 0 };
		}
	}
});

// 导出 createCustomComponent 函数供外部使用
export { createCustomComponent };

router.beforeEach((to, from, next) => {
	// 处理 URL 编码的空格
	if (to.fullPath.includes('%20')) {
		return next({ path: to.fullPath.replace(/%20/g, ''), replace: true });
	}

	// 验证语言参数
	if (to.params.lang) {
		if (!supportedLocales.includes(to.params.lang)) {
			const pathWithoutLang = to.path.replace(`/${to.params.lang}`, '') || '/index';
			return next({ path: pathWithoutLang, replace: true });
		}
		if (to.meta?.locale === 'dynamic') {
			to.meta.locale = to.params.lang;
		}
	}

	
	// 处理从有语言参数的路由跳转到无语言参数的路由
	if (from.params?.lang && !to.params?.lang && to.name) {
		const lang = from.params.lang;
		const fromPath = from.path.replace(`/${lang}`, '') || '/index';
		const toPath = to.path === '/' ? '/index' : to.path;
		
		// 如果目标路径已包含语言前缀，直接允许跳转
		if (to.path.startsWith(`/${lang}/`) || to.path === `/${lang}`) {
			return next();
		}
		
		// 如果当前语言是简体（ZH），且目标路径等于从源路径移除语言前缀后的路径
		// 说明这是语言切换操作（从英文切换到简体），允许跳转到无语言前缀的路由
		if (i18n.locale === ZH && fromPath === toPath) {
			return next();
		}
		
		// 其他所有情况（包括导航跳转），在英文模式下都应该保持语言前缀
		return next({
			path: `/${lang}${toPath}`,
			query: to.query,
			hash: to.hash,
			replace: false
		});
	}

	// 处理根路径重定向
	if (to.path === '/') {
		return next({ path: '/index', replace: true });
	}

	// 处理发布环境相关的 Cookie
	if (isRelease) {
		setCookie('release', 'pro_001');
		setCookie('symbol', 'clean');
	} else {
		removeCookie('release');
		removeCookie('symbol');
	}

	next();
});

export default router;
